import { Link } from "react-router-dom";
import { Bug, ArrowRight, Shield, Clock, MessageSquare } from "lucide-react";
import { Button } from "../components/ui/button";

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img 
            src="https://www.shoperator.ai/assets/shoperator_logo-BmLJu6La.svg" 
            alt="Shoperator" 
            className="h-8"
            data-testid="logo"
          />
          <Link to="/login">
            <Button 
              className="bg-[#4DB43E] hover:bg-[#3da030] text-white rounded-full px-6"
              data-testid="header-login-btn"
            >
              Sign In
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <main className="max-w-7xl mx-auto px-6 py-12 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
          {/* Hero Text */}
          <div className="col-span-full md:col-span-7 space-y-6 animate-slide-up">
            <div className="inline-flex items-center gap-2 bg-green-50 text-[#4DB43E] px-4 py-2 rounded-full text-sm font-medium">
              <Shield className="w-4 h-4" />
              Quality Assurance Portal
            </div>
            
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 text-heading"
              data-testid="hero-title"
            >
              Testing Results of <br />
              <span className="text-[#4DB43E]">Shoperator AI APP</span>
            </h1>
            
            <p className="text-lg text-gray-600 max-w-xl leading-relaxed">
              Stay informed about the testing progress and known issues. 
              View detailed bug reports, track resolutions, and share your feedback with our team.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/login">
                <Button 
                  className="bg-[#4DB43E] hover:bg-[#3da030] text-white rounded-full px-8 py-6 text-lg font-medium transition-all shadow-sm hover:shadow-lg active:scale-95 flex items-center gap-2"
                  data-testid="hero-login-btn"
                >
                  Sign In to View Reports
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Hero Visual */}
          <div className="col-span-full md:col-span-5 relative">
            <div className="relative h-[400px] md:h-[500px] rounded-2xl overflow-hidden bg-gradient-to-br from-green-50 to-gray-100 border border-gray-200">
              <img 
                src="https://images.pexels.com/photos/7307566/pexels-photo-7307566.jpeg" 
                alt="Testing" 
                className="absolute inset-0 w-full h-full object-cover opacity-40"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center space-y-4 p-8">
                  <div className="w-20 h-20 bg-white rounded-2xl shadow-lg mx-auto flex items-center justify-center">
                    <Bug className="w-10 h-10 text-[#4DB43E]" />
                  </div>
                  <p className="text-gray-700 font-medium">Bug Tracking Dashboard</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 md:mt-24">
          <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-[#4DB43E]/50 hover:shadow-lg transition-all">
            <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mb-4">
              <Bug className="w-6 h-6 text-[#4DB43E]" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2 text-heading">Detailed Bug Reports</h3>
            <p className="text-gray-600">View comprehensive bug reports with screenshots, videos, and reproduction steps.</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-[#4DB43E]/50 hover:shadow-lg transition-all">
            <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mb-4">
              <Clock className="w-6 h-6 text-[#4DB43E]" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2 text-heading">Real-time Status</h3>
            <p className="text-gray-600">Track the status of each issue from discovery to resolution in real-time.</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-[#4DB43E]/50 hover:shadow-lg transition-all">
            <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mb-4">
              <MessageSquare className="w-6 h-6 text-[#4DB43E]" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2 text-heading">Add Comments</h3>
            <p className="text-gray-600">Share your feedback and additional information directly on bug reports.</p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white mt-20">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <img 
            src="https://www.shoperator.ai/assets/shoperator_logo-BmLJu6La.svg" 
            alt="Shoperator" 
            className="h-6"
          />
          <p className="text-sm text-gray-500">
            Quality Assurance Portal for Shoperator AI
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
